import React, { Fragment, useEffect, useState } from 'react';
import Colors from '../../Assets/Styles/Colors';
import { Box, Button, Chip, Collapse, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Edit, FilterAltTwoTone, KeyboardArrowLeft, KeyboardArrowRight, Paid, QuestionAnswer, RemoveRedEye } from '@mui/icons-material';
import BreadCrumbs from '../../Components/BreadCrumbs/BreadCrumbs';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.module.css"
import "../../../App.css"
import { useTheme } from '@emotion/react';
import OnholdServices from '../../Api/Onhold/OnholdServices';
import SimpleDialog from '../../Components/Dialogs/SimpleDialog';
import { useForm } from 'react-hook-form';
import { Error } from '../../Components/UI/Error';
import { PrimaryButton } from '../../Components/Buttons';
import { InputField } from '../../Components/Fields/TextFields';
import { ErrorToaster, SuccessToaster } from '../../Components/Toaster/Toaster';
import { formatDate } from '../../Utils/FormatDate';

const tableHead = [
  "Job Name",
  "Budget",
  "Posted By",
  "Completed By",
  "Actions"
];

function DataFilter(props) {
  const {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    sortData,
  } = props;
  const [filterCollapse, setFilterCollapse] = useState(true);

  const filterData = () => {
    sortData(fromDate && new Date(fromDate), toDate && new Date(toDate));
  };

  const clearData = () => {
    setFromDate(null);
    setToDate(null);
    sortData();
  };

  return (
    <Box sx={{ bgcolor: Colors.white, p: 2, borderRadius: "12px", mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body1" fontWeight="bold" color="initial">
          Filters
        </Typography>
        <IconButton
          onClick={() => setFilterCollapse(!filterCollapse)}
          color="primary"
        >
          <FilterAltTwoTone sx={{ color: Colors.primary }} />
        </IconButton>
      </Box>

      <Collapse in={filterCollapse}>
        <Grid container spacing={2} alignItems={"flex-end"}>
          <Grid item md={6}>
            <DatePicker
              showIcon
              toggleCalendarOnIconClick
              selected={fromDate}
              onChange={(update) => {
                setFromDate(update);
              }}
              isClearable={true}
              placeholderText="Applied Date (From)"
              icon={
                <svg
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  height="1em"
                  width="1em"
                >
                  <path d="M4 .5a.5.5 0 00-1 0V1H2a2 2 0 00-2 2v1h16V3a2 2 0 00-2-2h-1V.5a.5.5 0 00-1 0V1H4V.5zM16 7V5H0v5h5a1 1 0 110 2H0v2a2 2 0 002 2h12a2 2 0 002-2V9h-6a1 1 0 110-2h6z" />
                </svg>
              }
            />
          </Grid>

          <Grid item md={6}>
            <DatePicker
              showIcon
              toggleCalendarOnIconClick
              selected={toDate}
              onChange={(update) => {
                setToDate(update);
              }}
              isClearable={true}
              placeholderText="Applied Date (To)"
              icon={
                <svg
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  height="1em"
                  width="1em"
                >
                  <path d="M4 .5a.5.5 0 00-1 0V1H2a2 2 0 00-2 2v1h16V3a2 2 0 00-2-2h-1V.5a.5.5 0 00-1 0V1H4V.5zM16 7V5H0v5h5a1 1 0 110 2H0v2a2 2 0 002 2h12a2 2 0 002-2V9h-6a1 1 0 110-2h6z" />
                </svg>
              }
            />
          </Grid>

          <Grid item xs={12} container columnSpacing={2} justifyContent="flex-end">
            <Button
              onClick={clearData}
              variant="outlined"
              sx={{
                mx: 1,
                textTransform: "capitalize",
                minWidth: "100px",
                boxShadow:
                  `0px 3px 1px -2px rgb(0 0 0 / 20%), 
                  0px 2px 2px 0px rgb(0 0 0 / 14%),
                  0px 1px 5px 0px rgb(0 0 0 / 12%)`,
              }}
            >
              Clear
            </Button>
            <Button
              onClick={filterData}
              variant="contained"
              sx={{
                textTransform: "capitalize",
                minWidth: "100px",
                boxShadow:
                  `0px 3px 1px -2px rgb(0 0 0 / 20%),
                  0px 2px 2px 0px rgb(0 0 0 / 14%),
                  0px 1px 5px 0px rgb(0 0 0 / 12%)`,
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}

function TablePaginationActions(props) {
  const theme = useTheme;

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </Box>
  );
}

function Onhold() {
  const [onholdJobs, setOnholdJobs] = useState([]);
  const [count, setCount] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [data, setData] = useState(null);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  console.log("🚀 ~ Onhold ~ selectedPayee:", selectedPayee)
  const { register, handleSubmit, formState: { errors }, reset } = useForm();


  const navigate = useNavigate();

  const handleTransferDialog = () => setIsTransferDialogOpen((open) => !open);

  const payees = [
    {
      name: "Employer",
      id: data != null ? data?.employer?._id : "",
      job_id: data != null ? data?.ad?._id : "",
    },
    {
      name: "Employee",
      id: data != null ? data?.employee?._id : "",
      job_id: data != null ? data?.ad?._id : "",
    }
  ]

  const getAll = async (page, limit, from, to) => {
    try {
      const result = await OnholdServices.getAll(
        page ? page : 1,
        limit ? limit : 10,
        from ? formatDate(from) : "",
        to ? formatDate(to) : ""
      );
      if (result) {
        setOnholdJobs(result?.onholdAds);
        setCount(result?.total);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const payToUser = async (formData) => {
    setIsLoading(true);
    const obj = {
      ad: selectedPayee?.job_id,
      user: selectedPayee?.id,
      amount: +formData.amount
    }
    try {
      const result = await OnholdServices.payToUser(obj);
      if (result.status == "success") {
        SuccessToaster(result.message);
        handleTransferDialog();
        reset();
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getAll(newPage + 1, rows, fromDate, toDate);
  };

  const handleChangeRowsPerPage = (event) => {
    setRows(Math.floor(event.target.value));
    setPage(0);
    getAll(page, event.target.value, fromDate, toDate);
  };

  const sort = (from, to) => {
    getAll(page, rows, from, to)
  }

  useEffect(() => {
    getAll();
  }, []);

  return (
    <Fragment>
      <SimpleDialog
        open={isTransferDialogOpen}
        title={"Transfer payment to?"}
        onClose={handleTransferDialog}
      >
        <Grid
          container
          spacing={2}
          component={"form"}
          onSubmit={handleSubmit(payToUser)}
        >
          <Grid item md={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel error={errors?.payee?.message}>{"Select Payee"}</InputLabel>
              <Select
                value={payees.find((p) => p.id === selectedPayee?._id)?.id}
                label={"Select Payee"}
                {...register("payee", {
                  required: "Please select user to pay",
                  onChange: (e) => {
                    const selected = payees.find((p) => p.id == e.target.value);
                    setSelectedPayee(selected)
                  },
                })}
                error={errors?.payee && true}
              >
                {payees?.map((user, index) => (
                  <MenuItem
                    key={index}
                    value={user?.id}
                  >
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
              {errors?.payee && <Error message={errors?.payee?.message} />}
            </FormControl>
          </Grid>
          <Grid item md={12}>
            <InputField
              label={"Amount"}
              register={register("amount", {
                required: "Please enter an amount"
              })}
              error={errors?.amount && true}
              helperText={errors?.amount?.message}
            />
          </Grid>
          <Grid item md={12}>
            <PrimaryButton
              fullWidth={true}
              title={"Pay"}
              type={"submit"}
            />
          </Grid>
        </Grid>
      </SimpleDialog>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: Colors.primaryGradient,
          p: 2,
          borderRadius: "12px",
        }}
      >
        <Grid item xs={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <IconButton onClick={() => navigate(-1)}>
              <KeyboardArrowLeft sx={{ color: Colors.white }} />
            </IconButton>
            <Typography variant="body1" fontWeight="bold" sx={{ color: Colors.white }}>
              Onhold Jobs
            </Typography>
          </Box>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <BreadCrumbs currentPage={["Onhold Jobs"]} />
        </Grid>
      </Grid>
      <DataFilter
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        sortData={(from, to) => sort(from, to)}
      />
      <TableContainer
        component={Paper}
        sx={{
          // width: "calc(100vw - 320px)",
          "&::-webkit-scrollbar": {
            bgcolor: Colors.white,
            height: "8px",
            borderRadius: "10px",
            cursor: "pointer",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: Colors.primary,
            borderRadius: "10px",
            cursor: "pointer",
          },
          "&.MuiPaper-root": {
            borderRadius: "12px",
            mt: 2,
            boxShadow: 0,
          },
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow sx={{ bgcolor: Colors.lightGray }}>
              {tableHead.map((item, index) => (
                <TableCell key={index}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      textAlign: item == "Actions" && "center",
                      width: item == "Actions" ? "100%" : "130px",
                    }}
                  >
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {onholdJobs.map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  {row?.ad?.title}
                </TableCell>
                <TableCell>
                  {row?.ad?.budget}
                </TableCell>
                <TableCell>
                  {row?.employer?.name}
                </TableCell>
                <TableCell>
                  {row?.employee?.name}
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2
                    }}
                  >
                    <Tooltip title="Chats">
                      <IconButton
                        onClick={() => navigate("/conversations", { state: { userId: row?.employer?._id, adId: row?.ad?._id } })}
                      >
                        <QuestionAnswer sx={{ color: Colors.primary }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Pay to">
                      <IconButton
                        onClick={() => { handleTransferDialog(); setData(row) }}
                      >
                        <Paid sx={{ color: Colors.primary }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Table sx={{ marginTop: "12px" }}>
        <TablePagination
          sx={{
            borderBottom: "none",
            bgcolor: Colors.white,
            borderRadius: "12px",
          }}
          rowsPerPageOptions={[10, 20, 30]}
          colSpan={12}
          count={+count}
          rowsPerPage={rows}
          page={page}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Table>
    </Fragment>
  )
}

export default Onhold